import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { familiesData } from '../../data/familiesData';
import { rsvpService } from '../../services/rsvpService';
import Modal from './Modal';
import styles from './RSVP.module.css';

const RSVP: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedGuests, setSelectedGuests] = useState<string[]>([]);
  const [customName, setCustomName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info' as 'success' | 'error' | 'warning' | 'info'
  });
  
  const sectionRef = useRef<HTMLDivElement>(null);
  const { familyId } = useParams<{ familyId?: string }>();

  const familyData = familyId ? familiesData[familyId] : null;
  const isGenericInvite = !familyId;

 useEffect(() => {
  const observer = new IntersectionObserver(
    ([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
      }
    },
    {
      threshold: 0.2,
      rootMargin: '0px'
    }
  );

  const currentRef = sectionRef.current;

  if (currentRef) {
    observer.observe(currentRef);
  }

  return () => {
    if (currentRef) {
      observer.unobserve(currentRef);
    }
  };
}, []);

  const handleGuestToggle = (guestName: string) => {
    if (guestName === 'No podremos asistir' || guestName === 'No podré asistir') {
      setSelectedGuests([guestName]);
    } else {
      setSelectedGuests(prev => {
        const filtered = prev.filter(g => g !== 'No podremos asistir' && g !== 'No podré asistir');
        return prev.includes(guestName) 
          ? filtered.filter(g => g !== guestName)
          : [...filtered, guestName];
      });
    }
  };

  const showModal = (title: string, message: string, type: 'success' | 'error' | 'warning' | 'info') => {
    setModalConfig({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeModal = () => {
    setModalConfig(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirm = async () => {
    // Verificar configuración del servicio
    if (!rsvpService.isConfigured()) {
      showModal(
        'Configuración pendiente',
        'El sistema de confirmaciones aún no está configurado. Por favor, contacta al administrador.',
        'warning'
      );
      return;
    }

    setIsSubmitting(true);

    try {
      if (isGenericInvite) {
        // Validación para invitación genérica
        if (!customName.trim()) {
          showModal(
            'Información requerida',
            'Por favor, escribe tu nombre completo para confirmar tu asistencia.',
            'warning'
          );
          setIsSubmitting(false);
          return;
        }
        
        // Enviar a Google Sheets
        const success = await rsvpService.submitGenericRSVP(customName.trim());
        
        if (success) {
          showModal(
            '¡Gracias por confirmar!',
            `${customName}, hemos registrado tu asistencia. ¡Te esperamos!`,
            'success'
          );
          
          setTimeout(() => {
            setCustomName('');
          }, 2000);
        } else {
          showModal(
            'Error al confirmar',
            'Hubo un problema al registrar tu asistencia. Por favor, intenta de nuevo.',
            'error'
          );
        }
        
      } else if (familyData) {
        // Validación para familia específica
        if (selectedGuests.length === 0) {
          showModal(
            'Selección requerida',
            'Por favor, selecciona quiénes asistirán antes de confirmar.',
            'warning'
          );
          setIsSubmitting(false);
          return;
        }
        
        const isDecline = selectedGuests.includes('No podremos asistir') || selectedGuests.includes('No podré asistir');
        
        if (isDecline) {
          // Obtener los nombres de los invitados que declinan (excluyendo las opciones de inasistencia)
          const declineNames = familyData.guests
            .filter(g => g.name !== 'No podremos asistir' && g.name !== 'No podré asistir')
            .map(g => g.name)
            .join(', ');

          // Enviar declinación
          const success = await rsvpService.submitDecline(familyData.familyName, declineNames);
          
          if (success) {
            showModal(
              'Confirmación recibida',
              'Lamentamos que no puedan asistir. ¡Esperamos verlos pronto!',
              'info'
            );
          } else {
            showModal(
              'Error al confirmar',
              'Hubo un problema al registrar la información. Por favor, intenta de nuevo.',
              'error'
            );
          }
        } else {
          // Preparar datos de TODOS los invitados de la familia
          const allGuestsToSubmit = familyData.guests
            .filter(guest => guest.name !== 'No podremos asistir' && guest.name !== 'No podré asistir')
            .map(guest => ({
              name: guest.name,
              isChild: guest.isChild,
              confirmed: selectedGuests.includes(guest.name) // true si está seleccionado, false si no
            }));
          
          // Enviar confirmación con todos los invitados
          const success = await rsvpService.submitFamilyRSVP(
            familyData.familyName,
            allGuestsToSubmit
          );
          
          if (success) {
            const confirmedCount = allGuestsToSubmit.filter(g => g.confirmed).length;
            const guestText = confirmedCount === 1 ? 'invitado' : 'invitados';
            
            showModal(
              '¡Gracias por confirmar!',
              `Hemos registrado la asistencia de ${confirmedCount} ${guestText}. ¡Los esperamos!`,
              'success'
            );
          } else {
            showModal(
              'Error al confirmar',
              'Hubo un problema al registrar la asistencia. Por favor, intenta de nuevo.',
              'error'
            );
          }
        }
        
        setTimeout(() => {
          setSelectedGuests([]);
        }, 2000);
      }
    } catch (error) {
      console.error('Error en confirmación:', error);
      showModal(
        'Error inesperado',
        'Ocurrió un error al procesar tu confirmación. Por favor, intenta más tarde.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section className={styles.rsvp} ref={sectionRef}>
        <div className={styles.topDecoration}></div>
        <div className="container">
          <div className={`${styles.rsvpCard} ${isVisible ? styles.fadeIn : ''}`}>
            {isGenericInvite ? (
              // Vista para invitación genérica (sin parámetro de ruta)
              <>
                <h3>Confirma tu asistencia</h3>
                <input 
                  type="text" 
                  placeholder="Escribe tu nombre completo"
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  className={styles.familyInput}
                  disabled={isSubmitting}
                />
                <button 
                  className={styles.confirmButton}
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'ENVIANDO...' : 'CONFIRMAR ASISTENCIA'}
                </button>
              </>
            ) : familyData ? (
              // Vista para familia específica
              <>
                <h3>Invitación para:</h3>
                <div className={styles.familyNameDisplay}>
                  {familyData.familyName}
                </div>
                
                <div className={styles.guestSelection}>
                  {familyData.guests.map((guest) => (
                    <label key={guest.name} className={styles.guestOption}>
                      <input
                        type="checkbox"
                        checked={selectedGuests.includes(guest.name)}
                        onChange={() => handleGuestToggle(guest.name)}
                        disabled={isSubmitting}
                      />
                      <span className={styles.checkmark}></span>
                      {guest.name}
                      {guest.isChild && <span className={styles.childBadge}></span>}
                    </label>
                  ))}
                </div>
                
                <button 
                  className={styles.confirmButton}
                  onClick={handleConfirm}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'ENVIANDO...' : 'CONFIRMAR'}
                </button>
              </>
            ) : (
              // Familia no encontrada
              <div className={styles.notFound}>
                <h3>Invitación no encontrada</h3>
                <p>Lo sentimos, no pudimos encontrar esta invitación.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <Modal
        isOpen={modalConfig.isOpen}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
        type={modalConfig.type}
      />
    </>
  );
};

export default RSVP;